<template>
  <div class="d-flex flex-column ga-4">
    <v-alert type="info">
      {{ $t("ncrm.integrations.externalApi.v4Notice") }}
    </v-alert>

    <v-alert v-if="errorMessage" type="error">
      {{ errorMessage }}
    </v-alert>

    <v-alert v-if="successMessage" type="success">
      {{ successMessage }}
    </v-alert>

    <v-row>
      <v-col
        v-for="card in overviewCards"
        :key="card.key"
        cols="12"
        sm="6"
        lg="3"
      >
        <v-card variant="outlined" class="pa-4 ncrm-external-api-workspace__metric-card">
          <div class="text-caption text-medium-emphasis d-flex align-center justify-space-between">
            <span>{{ card.label }}</span>
            <v-icon size="16" :color="card.color">{{ card.icon }}</v-icon>
          </div>
          <div class="text-h5 font-weight-bold mt-3">
            {{ card.value }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" xl="4">
        <NcrmExternalApiEndpointListCard
          :endpoints="endpoints"
          :selected-endpoint-id="selectedEndpointId"
          :loading="loadingWorkspace"
          @create="openCreateDialog"
          @select="openEditDialog"
        />
      </v-col>

      <v-col cols="12" xl="8">
        <NcrmExternalApiContractCard :contract="contract" />
      </v-col>
    </v-row>

    <NcrmExternalApiDeliveriesCard
      class="ncrm-external-api-workspace__deliveries"
      :deliveries="deliveries"
      :selected-delivery="selectedDelivery"
      :selected-delivery-id="selectedDeliveryId"
      :loading="loadingDeliveries"
      :pinned-delivery-id="initialDeliveryId"
      @refresh="refreshDeliveries"
      @select-delivery="handleSelectDelivery"
    />

    <NcrmRecordEditDialog
      v-model="editorDialog"
      :title="editorDialogTitle"
      :subtitle="editorDialogSubtitle"
      :loading="dialogSaving"
      :max-width="980"
      :save-label="editorDialogSaveLabel"
      @save="handleDialogSave"
    >
      <NcrmExternalApiEndpointEditorCard
        ref="dialogEndpointEditorCard"
        :endpoint="dialogEndpoint"
        :templates="templates"
        :saving="dialogSaving"
        :keys-busy="keysBusy"
        :keys="dialogKeys"
        :generated-secret="generatedSecret"
        :key-error="keyManagementError"
        :status-options="workspace.endpoint_statuses || []"
        :show-header="false"
        :show-save-action="false"
        @generate-key="handleGenerateKey"
        @revoke-key="handleRevokeKey"
        @delete-key="handleDeleteKey"
      />
    </NcrmRecordEditDialog>
  </div>
</template>

<script>
import axios from "axios";

import NcrmExternalApiContractCard from "@/components/ncrm/integrations/NcrmExternalApiContractCard.vue";
import NcrmExternalApiDeliveriesCard from "@/components/ncrm/integrations/NcrmExternalApiDeliveriesCard.vue";
import NcrmExternalApiEndpointEditorCard from "@/components/ncrm/integrations/NcrmExternalApiEndpointEditorCard.vue";
import NcrmExternalApiEndpointListCard from "@/components/ncrm/integrations/NcrmExternalApiEndpointListCard.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  cloneExternalApiEndpoint,
  createExternalApiEndpointDraft,
} from "@/Utile/ncrmExternalApi.mjs";

export default {
  name: "NcrmExternalApiWorkspace",
  props: {
    initialDeliveryId: {
      type: Number,
      default: null,
    },
  },
  components: {
    NcrmExternalApiContractCard,
    NcrmExternalApiDeliveriesCard,
    NcrmExternalApiEndpointEditorCard,
    NcrmExternalApiEndpointListCard,
    NcrmRecordEditDialog,
  },
  data() {
    return {
      loadingWorkspace: false,
      loadingDeliveries: false,
      savingEndpoint: false,
      savingCreateEndpoint: false,
      keysBusy: false,
      errorMessage: "",
      keyManagementError: "",
      successMessage: "",
      workspace: {},
      overview: {
        received_24h: 0,
        failed_24h: 0,
        received_7d: 0,
        failed_7d: 0,
      },
      endpoints: [],
      templates: [],
      contract: {},
      selectedEndpointId: null,
      selectedEndpoint: createExternalApiEndpointDraft(),
      endpointKeys: [],
      deliveries: [],
      selectedDeliveryId: null,
      selectedDelivery: null,
      generatedSecret: "",
      editorDialog: false,
      editorMode: "create",
      createEndpointDraft: createExternalApiEndpointDraft(),
    };
  },
  computed: {
    overviewCards() {
      return [
        {
          key: "received_24h",
          label: this.$t("ncrm.integrations.externalApi.cards.received24h"),
          value: this.overview.received_24h || 0,
          icon: "mdi-arrow-down-circle-outline",
          color: "primary",
        },
        {
          key: "failed_24h",
          label: this.$t("ncrm.integrations.externalApi.cards.failed24h"),
          value: this.overview.failed_24h || 0,
          icon: "mdi-alert-circle-outline",
          color: "error",
        },
        {
          key: "received_7d",
          label: this.$t("ncrm.integrations.externalApi.cards.received7d"),
          value: this.overview.received_7d || 0,
          icon: "mdi-calendar-range-outline",
          color: "info",
        },
        {
          key: "failed_7d",
          label: this.$t("ncrm.integrations.externalApi.cards.failed7d"),
          value: this.overview.failed_7d || 0,
          icon: "mdi-alert-decagram-outline",
          color: "warning",
        },
      ];
    },
    dialogEndpoint() {
      return this.editorMode === "edit"
        ? this.selectedEndpoint
        : this.createEndpointDraft;
    },
    dialogKeys() {
      return this.editorMode === "edit" ? this.endpointKeys : [];
    },
    dialogSaving() {
      return this.editorMode === "edit" ? this.savingEndpoint : this.savingCreateEndpoint;
    },
    editorDialogTitle() {
      return this.editorMode === "edit"
        ? this.$t("ncrm.integrations.externalApi.editor.titleEdit")
        : this.$t("ncrm.integrations.externalApi.editor.titleCreate");
    },
    editorDialogSubtitle() {
      return this.editorMode === "edit"
        ? this.$t("ncrm.integrations.externalApi.editor.subtitleEdit")
        : this.$t("ncrm.integrations.externalApi.editor.subtitleCreate");
    },
    editorDialogSaveLabel() {
      return this.editorMode === "edit"
        ? this.$t("ncrm.integrations.externalApi.editor.actions.save")
        : this.$t("app.actions.create");
    },
  },
  async mounted() {
    await this.fetchWorkspace();
    if (this.initialDeliveryId) {
      await this.handleSelectDelivery(this.initialDeliveryId);
      await this.$nextTick();
      this.$el?.querySelector?.(".ncrm-external-api-workspace__deliveries")?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
    }
  },
  methods: {
    clearMessages() {
      this.errorMessage = "";
      this.keyManagementError = "";
      this.successMessage = "";
    },
    async focusDialogEditor() {
      await this.$nextTick();
      this.$refs.dialogEndpointEditorCard?.focusNameField?.();
    },
    openCreateDialog() {
      this.clearMessages();
      this.editorMode = "create";
      this.generatedSecret = "";
      this.createEndpointDraft = createExternalApiEndpointDraft(this.templates?.[0] || null);
      this.editorDialog = true;
      this.focusDialogEditor();
    },
    async openEditDialog(endpointId) {
      const loaded = await this.handleSelectEndpoint(endpointId);
      if (!loaded) return;
      this.editorMode = "edit";
      this.editorDialog = true;
      this.focusDialogEditor();
    },
    async fetchWorkspace(options = {}) {
      const { preserveGeneratedSecret = false } = options;
      this.loadingWorkspace = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get("/ncrm/integrations/external-api/workspace");
        this.workspace = data?.workspace || {};
        this.overview = data?.overview || this.overview;
        this.endpoints = Array.isArray(data?.endpoints) ? data.endpoints : [];
        this.templates = Array.isArray(data?.templates) ? data.templates : [];
        this.contract = data?.contract || {};
        await this.hydrateSelectionAfterWorkspace({ preserveGeneratedSecret });
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.integrations.externalApi.loadError");
      } finally {
        this.loadingWorkspace = false;
      }
    },
    async hydrateSelectionAfterWorkspace(options = {}) {
      const { preserveGeneratedSecret = false } = options;
      if (this.selectedEndpointId) {
        const matched = this.endpoints.find((endpoint) => endpoint.id === this.selectedEndpointId);
        if (matched) {
          await this.handleSelectEndpoint(this.selectedEndpointId, {
            silent: true,
            preserveGeneratedSecret,
          });
          return;
        }
      }

      if (this.endpoints.length) {
        await this.handleSelectEndpoint(this.endpoints[0].id, {
          silent: true,
          preserveGeneratedSecret,
        });
        return;
      }

      this.selectedEndpointId = null;
      this.selectedEndpoint = null;
      this.endpointKeys = [];
      await this.fetchDeliveries();
    },
    async handleSelectEndpoint(endpointId, { silent = false, preserveGeneratedSecret = false } = {}) {
      if (!preserveGeneratedSecret) {
        this.generatedSecret = "";
      }
      this.selectedEndpointId = endpointId;
      if (!silent) {
        this.clearMessages();
      }
      try {
        const { data } = await axios.get(`/ncrm/integrations/external-api/endpoints/${endpointId}`);
        this.selectedEndpoint = cloneExternalApiEndpoint(data?.endpoint || {});
        this.endpointKeys = Array.isArray(data?.keys) ? data.keys : [];
        this.deliveries = Array.isArray(data?.deliveries) ? data.deliveries : [];
        this.selectedDeliveryId = null;
        this.selectedDelivery = null;
        return true;
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          this.$t("ncrm.integrations.externalApi.editor.loadError");
        return false;
      }
    },
    async handleSaveSelectedEndpoint(payload) {
      this.savingEndpoint = true;
      this.clearMessages();
      try {
        const { data } = await axios.patch(
          `/ncrm/integrations/external-api/endpoints/${this.selectedEndpointId}`,
          payload
        );
        this.selectedEndpoint = cloneExternalApiEndpoint(data?.endpoint || {});
        this.successMessage = this.$t("ncrm.integrations.externalApi.editor.updated");
        await this.fetchWorkspace();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          this.$t("ncrm.integrations.externalApi.editor.saveError");
      } finally {
        this.savingEndpoint = false;
      }
    },
    async submitCreateEndpoint() {
      this.savingCreateEndpoint = true;
      this.clearMessages();
      try {
        const payload = this.buildDialogEndpointPayload();
        if (!payload) return;
        const { data } = await axios.post("/ncrm/integrations/external-api/endpoints", payload);
        this.selectedEndpointId = data?.endpoint?.id || null;
        this.selectedEndpoint = cloneExternalApiEndpoint(data?.endpoint || {});
        this.endpointKeys = [];
        this.deliveries = [];
        this.selectedDeliveryId = null;
        this.selectedDelivery = null;
        this.editorMode = "edit";
        this.successMessage = this.$t("ncrm.integrations.externalApi.editor.created");
        await this.fetchWorkspace();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t("ncrm.integrations.externalApi.editor.saveError");
      } finally {
        this.savingCreateEndpoint = false;
      }
    },
    buildDialogEndpointPayload() {
      try {
        return this.$refs.dialogEndpointEditorCard?.buildPayloadForSave?.();
      } catch (error) {
        const message =
          error?.message ||
          this.$t("ncrm.integrations.externalApi.editor.saveError");
        this.errorMessage = message;
        this.$refs.dialogEndpointEditorCard?.setFormError?.(message);
        return null;
      }
    },
    async handleDialogSave() {
      if (this.editorMode === "edit") {
        this.clearMessages();
        const payload = this.buildDialogEndpointPayload();
        if (!payload) return;
        await this.handleSaveSelectedEndpoint(payload);
        return;
      }
      await this.submitCreateEndpoint();
    },
    async handleGenerateKey(label) {
      if (!this.selectedEndpointId) return;
      this.keysBusy = true;
      this.clearMessages();
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/external-api/endpoints/${this.selectedEndpointId}/keys`,
          { label }
        );
        this.generatedSecret = data?.generated_secret || "";
        this.endpointKeys = Array.isArray(data?.keys) ? data.keys : [];
        this.successMessage = this.$t("ncrm.integrations.externalApi.keys.generated");
        await this.fetchWorkspace({ preserveGeneratedSecret: true });
      } catch (error) {
        this.keyManagementError =
          error?.response?.data?.error ||
          this.$t("ncrm.integrations.externalApi.keys.generateError");
      } finally {
        this.keysBusy = false;
      }
    },
    async handleRevokeKey(keyId) {
      if (!this.selectedEndpointId) return;
      this.keysBusy = true;
      this.clearMessages();
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/external-api/endpoints/${this.selectedEndpointId}/keys/${keyId}/revoke`
        );
        this.endpointKeys = Array.isArray(data?.keys) ? data.keys : [];
        this.successMessage = this.$t("ncrm.integrations.externalApi.keys.revoked");
        await this.fetchWorkspace();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          this.$t("ncrm.integrations.externalApi.keys.revokeError");
      } finally {
        this.keysBusy = false;
      }
    },
    async handleDeleteKey(keyId) {
      if (!this.selectedEndpointId) return;
      this.keysBusy = true;
      this.clearMessages();
      try {
        const { data } = await axios.delete(
          `/ncrm/integrations/external-api/endpoints/${this.selectedEndpointId}/keys/${keyId}`
        );
        this.endpointKeys = Array.isArray(data?.keys) ? data.keys : [];
        this.successMessage = this.$t("ncrm.integrations.externalApi.keys.deleted");
        await this.fetchWorkspace();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          this.$t("ncrm.integrations.externalApi.keys.deleteError");
      } finally {
        this.keysBusy = false;
      }
    },
    async fetchDeliveries(endpointId = null) {
      this.loadingDeliveries = true;
      try {
        const params = endpointId ? { endpointId } : {};
        const { data } = await axios.get("/ncrm/integrations/external-api/deliveries", { params });
        this.deliveries = Array.isArray(data?.deliveries) ? data.deliveries : [];
        this.selectedDeliveryId = null;
        this.selectedDelivery = null;
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          this.$t("ncrm.integrations.externalApi.deliveries.loadError");
      } finally {
        this.loadingDeliveries = false;
      }
    },
    async refreshDeliveries() {
      await this.fetchDeliveries(this.selectedEndpointId);
    },
    async handleSelectDelivery(deliveryId) {
      this.loadingDeliveries = true;
      this.clearMessages();
      try {
        const { data } = await axios.get(`/ncrm/integrations/external-api/deliveries/${deliveryId}`);
        this.selectedDeliveryId = deliveryId;
        this.selectedDelivery = data?.delivery || null;
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          this.$t("ncrm.integrations.externalApi.deliveries.detailLoadError");
      } finally {
        this.loadingDeliveries = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-external-api-workspace__metric-card {
  min-height: 116px;
}
</style>
